'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

import { Menu, X } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface SiteNavProps {
  variant?: 'transparent' | 'white';
  currentPath?: string;
}

export default function SiteNav({ variant: _variant, currentPath = '' }: SiteNavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);

  useEffect(() => {
    const supabase = createClient();

    const checkPaidStatus = async (userId: string, sessionMetadata?: Record<string, unknown>) => {
      // 1. Check session metadata first (fastest, no extra network call)
      if (sessionMetadata?.has_paid === true) {
        setHasPaid(true);
        return;
      }

      // 2. Check user_profiles table
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_paid')
        .eq('id', userId)
        .maybeSingle();

      if (profile?.is_paid === true) {
        setHasPaid(true);
        return;
      }

      // 3. Fetch fresh user from server (catches metadata updated after login)
      try {
        const { data: { user: freshUser } } = await supabase.auth.getUser();
        if (freshUser?.user_metadata?.has_paid === true) {
          setHasPaid(true);
        }
      } catch {
        // ignore
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setIsLoggedIn(true);
        checkPaidStatus(session.user.id, session.user.user_metadata as Record<string, unknown>);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setIsLoggedIn(true);
        checkPaidStatus(session.user.id, session.user.user_metadata as Record<string, unknown>);
      } else {
        setIsLoggedIn(false);
        setHasPaid(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

    // Module pages are only accessible to paid users — treat logged-in users there as paid
  const isModulePage = currentPath.startsWith('/module/');
  const isPaymentConfirmation = currentPath === '/payment-confirmation';
  const isDashboard = currentPath === '/dashboard';
  const showDashboardButton = isPaymentConfirmation || isDashboard || (isLoggedIn && hasPaid) || (isLoggedIn && isModulePage);

  return (
    <>
      <nav
        className={`sticky top-0 left-0 right-0 z-50 border-b border-warm-border`}
        style={{ backgroundColor: '#F9F7F4' }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" height="32" width="32" aria-hidden="true" className="flex-shrink-0">
              <path d="M100 48 C78 48 60 66 60 88 C60 103 68 116 80 123 L80 132 C80 134 82 136 84 136 L116 136 C118 136 120 134 120 132 L120 123 C132 116 140 103 140 88 C140 66 122 48 100 48Z" fill="#D4891A" opacity="0.15"/>
              <path d="M100 48 C78 48 60 66 60 88 C60 103 68 116 80 123 L80 132 C80 134 82 136 84 136 L116 136 C118 136 120 134 120 132 L120 123 C132 116 140 103 140 88 C140 66 122 48 100 48Z" fill="none" stroke="#D4891A" strokeWidth="3.5" strokeLinejoin="round"/>
              <path d="M100 54 C81 54 66 69 66 88 C66 101 73 112 84 119 L84 130 L116 130 L116 119 C127 112 134 101 134 88 C134 69 119 54 100 54Z" fill="#D4891A" opacity="0.08"/>
              <line x1="100" y1="72" x2="100" y2="118" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="88" y1="82" x2="100" y2="72" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="112" y1="82" x2="100" y2="72" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="88" y1="98" x2="88" y2="82" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="112" y1="98" x2="112" y2="82" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="88" y1="98" x2="100" y2="118" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <line x1="112" y1="98" x2="100" y2="118" stroke="#D4891A" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <rect x="82" y="138" width="36" height="7" rx="2" fill="#D4891A" opacity="0.9"/>
              <rect x="82" y="148" width="36" height="7" rx="2" fill="#D4891A" opacity="0.7"/>
              <rect x="86" y="158" width="28" height="5" rx="2.5" fill="#D4891A" opacity="0.5"/>
              <line x1="58" y1="58" x2="50" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
              <line x1="50" y1="58" x2="58" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
              <line x1="142" y1="58" x2="150" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
              <line x1="150" y1="58" x2="142" y2="50" stroke="#D4891A" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
            </svg>
            <span
              className="font-display text-xl font-semibold tracking-tight hidden sm:block"
              style={{ color: 'var(--navy)' }}
            >
              That Clicked
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              href="/free-lesson-screen"
              className="font-body text-sm font-medium transition-colors duration-150 hover:text-amber-500"
              style={{ color: currentPath === '/free-lesson-screen' ? 'var(--amber)' : 'var(--navy-muted)' }}
            >
              Free Lessons
            </Link>
            <Link
              href="/conversion-page"
              className="font-body text-sm font-medium transition-colors duration-150 hover:text-amber-500"
              style={{ color: currentPath === '/conversion-page' ? 'var(--amber)' : 'var(--navy-muted)' }}
            >
              Pricing
            </Link>
            <Link
              href="/dashboard"
              className="font-body text-sm font-medium transition-colors duration-150 hover:text-amber-500"
              style={{ color: currentPath === '/dashboard' ? 'var(--amber)' : 'var(--navy-muted)' }}
            >
              My Dashboard
            </Link>
            <Link
              href="/blog"
              className="font-body text-sm font-medium transition-colors duration-150 hover:text-amber-500"
              style={{ color: currentPath === '/blog' || currentPath.startsWith('/blog/') ? 'var(--amber)' : 'var(--navy-muted)' }}
            >
              Blog
            </Link>
            {isLoggedIn && (
              <Link
                href="/account"
                className="font-body text-sm font-medium transition-colors duration-150 hover:text-amber-500"
                style={{ color: currentPath === '/account' ? 'var(--amber)' : 'var(--navy-muted)' }}
              >
                Account
              </Link>
            )}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {showDashboardButton ? (
              <Link
                href="/dashboard"
                className="font-body text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 active:scale-95"
                style={{ backgroundColor: 'var(--navy)', color: 'white' }}
              >
                My Dashboard
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  className="font-body text-sm font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-navy-50 active:scale-95"
                  style={{ color: 'var(--navy)' }}
                >
                  Log in
                </Link>
                <div className="flex flex-col items-center">
                  <Link
                    href="/free-lesson-screen"
                    className="font-body text-sm font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-navy-50 active:scale-95"
                    style={{ color: 'var(--navy)' }}
                  >
                    Start Free
                  </Link>
                  <span className="font-body text-xs" style={{ color: 'var(--text-light)', fontSize: '0.65rem', marginTop: '-2px' }}>
                    No credit card required
                  </span>
                </div>
                <Link
                  href="/conversion-page"
                  className="font-body text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-150 active:scale-95 shadow-amber-glow"
                  style={{ backgroundColor: 'var(--amber)', color: 'white' }}
                >
                  Get Full Access — $67
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg transition-colors duration-150 hover:bg-navy-50"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            style={{ color: 'var(--navy)' }}
          >
            <Menu size={22} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 animate-fade-in"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-72 bg-white shadow-warm-lg flex flex-col transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-warm-border">
          <span className="font-display text-lg font-semibold" style={{ color: 'var(--navy)' }}>
            That Clicked
          </span>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-lg hover:bg-warm-muted transition-colors duration-150"
            aria-label="Close navigation menu"
          >
            <X size={20} style={{ color: 'var(--navy-muted)' }} />
          </button>
        </div>

        <div className="flex-1 px-6 py-8 flex flex-col gap-2">
          {[
            { label: 'Free Lessons', href: '/free-lesson-screen' },
            { label: 'Pricing', href: '/conversion-page' },
            { label: 'My Dashboard', href: '/dashboard' },
            { label: 'Blog', href: '/blog' },
            { label: 'About', href: '/about' },
            ...(isLoggedIn ? [{ label: 'Account', href: '/account' }] : []),
            { label: 'Log in', href: '/login' },
          ].map((item) => (
            <Link
              key={`mobile-nav-${item.label}`}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="font-body text-base font-medium px-4 py-3 rounded-lg transition-colors duration-150 hover:bg-warm-muted"
              style={{ color: 'var(--navy)' }}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="px-6 pb-8 flex flex-col gap-3">
          {showDashboardButton ? (
            <Link
              href="/dashboard"
              onClick={() => setMobileOpen(false)}
              className="font-body text-sm font-semibold px-5 py-3 rounded-lg text-center transition-all duration-150 active:scale-95"
              style={{ backgroundColor: 'var(--navy)', color: 'white' }}
            >
              My Dashboard
            </Link>
          ) : (
            <>
              <Link
                href="/login"
                onClick={() => setMobileOpen(false)}
                className="font-body text-sm font-medium px-5 py-3 rounded-lg text-center border transition-all duration-150 active:scale-95"
                style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}
              >
                Log in
              </Link>
              <Link
                href="/free-lesson-screen"
                onClick={() => setMobileOpen(false)}
                className="font-body text-sm font-medium px-5 py-3 rounded-lg text-center border transition-all duration-150 active:scale-95"
                style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}
              >
                Start Free
              </Link>
              <Link
                href="/conversion-page"
                onClick={() => setMobileOpen(false)}
                className="font-body text-sm font-semibold px-5 py-3 rounded-lg text-center transition-all duration-150 active:scale-95"
                style={{ backgroundColor: 'var(--amber)', color: 'white' }}
              >
                Get Full Access — $67
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}